import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@ntropy/utils/src/cn";
import { usePreviousDistinct } from "@ntropy/hooks";
import { isEventInsideElement, isHTMLElement } from "@ntropy/utils/src/react-utils";

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-[400] bg-black/40  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className
        )}
        data-overlay={true}
        {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const reactRoot = document.getElementById("react-root")!;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    persistChildrenOnAnimateOut?: boolean,
    slimPadding?: boolean
    zIndex?: number
}
>(({ className, children, persistChildrenOnAnimateOut = true, slimPadding, onInteractOutside, zIndex, ...props }, ref) => {
    const prevChildren = usePreviousDistinct(children); // for dialog animate-out animation
    const contentPaddingStyle = slimPadding ? "p-4 sm:p-6" : "p-6 sm:p-10"
    const closePaddingStyle = slimPadding ? "right-4 sm:right-6 top-5 sm:top-7" : "right-6 sm:right-8 top-9 sm:top-12"

    return (
        <DialogPortal>
            <DialogOverlay className={`z-[${zIndex}]`} />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    "fixed left-[50%] top-[50%] z-[400] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg overflow-y-auto overflow-x-hidden",
                    contentPaddingStyle,
                    !!zIndex && `z-[${zIndex}]`,
                    className,
                )}
                onInteractOutside={e => {
                    const isOverlay = isHTMLElement(e.target) && e.target.dataset.overlay;

                    if (!isOverlay && !isEventInsideElement(e, reactRoot)) {
                        e.preventDefault();
                    }

                    onInteractOutside?.(e);
                }}
                {...props}
            >
                {persistChildrenOnAnimateOut ? children ?? prevChildren : children}
                <DialogPrimitive.Close
                    className={cn(
                        "absolute rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
                        closePaddingStyle,
                    )}
                >
                    <X className="h-6 w-6" />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            </DialogPrimitive.Content>
        </DialogPortal>
    );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col space-y-1.5 text-center sm:text-left",
            className
        )}
        {...props}
    />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className
        )}
        {...props}
    />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn(
            "font-[700] text-[28px] leading-none tracking-tight",
            className
        )}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={className}
        {...props}
    />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
}
